import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const CHECKOUT_URL = "/api/checkout";

const SUPPORT_TIERS: readonly { emoji: string; name: string; amount: number; desc: string; recommended?: boolean }[] = [
  { emoji: "🏃", name: "Starter", amount: 500, desc: "Buy us a post-run coffee" },
  { emoji: "⭐", name: "Supporter", amount: 1000, desc: "Covers the cost of questions and materials", recommended: true },
  { emoji: "🏆", name: "Champion", amount: 2500, desc: "Help us grow Quizrun" },
  { emoji: "❤️", name: "Super Fan", amount: 5000, desc: "You're basically a third founder" },
] as const satisfies readonly { emoji: string; name: string; amount: number; desc: string; recommended?: boolean }[];

export function TierPicker() {
  const { user, displayName, personId } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState("");

  const handleCheckout = async (amount: number, tierName: string) => {
    setLoading(tierName);
    try {
      const body: Record<string, unknown> = {
        amount,
        tierName,
        email: user?.email,
        personId,
        name: displayName,
      };
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setLoading(null);
        return;
      }
      const { url } = await res.json();
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* stay on page */
    } finally {
      setLoading(null);
    }
  };

  const customPounds = parseFloat(customAmount);
  const customValid = Number.isFinite(customPounds) && customPounds >= 1;

  return (
    <div>
      <div className="grid gap-2">
        {SUPPORT_TIERS.map((tier) => (
          <button
            type="button"
            key={tier.name}
            onClick={() => handleCheckout(tier.amount, tier.name)}
            disabled={loading !== null}
            className={`relative flex items-center gap-3 p-5 rounded-xl border transition-colors active:bg-accent/70 disabled:opacity-60 disabled:cursor-not-allowed ${
              tier.recommended
                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                : "bg-card hover:bg-accent/50"
            }`}
          >
            {tier.recommended && (
              <span className="absolute -top-2.5 right-4 text-[11px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2.5 py-0.5 rounded-full">
                Popular
              </span>
            )}
            <span className="text-3xl" aria-hidden="true">{tier.emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="text-lg font-bold">{tier.name}</div>
              <div className="text-sm text-muted-foreground">{tier.desc}</div>
            </div>
            <div className="text-xl font-black text-primary shrink-0">
              {loading === tier.name ? "…" : `£${tier.amount / 100}`}
            </div>
          </button>
        ))}
      </div>

      {/* Custom amount */}
      <div className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
          <input
            type="number"
            min="1"
            step="1"
            placeholder="Other amount"
            aria-label="Custom amount in pounds"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            className="w-full h-10 pl-7 pr-3 rounded-xl border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            if (customValid) handleCheckout(Math.round(customPounds * 100), "Custom");
          }}
          disabled={loading !== null || !customValid}
          className="h-10 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          {loading === "Custom" ? "…" : "Pay"}
        </button>
      </div>

      <p className="text-center text-[11px] text-muted-foreground mt-2">
        Powered by Stripe
      </p>
    </div>
  );
}
